import {
  ArrowRight01Icon,
  LicenseIcon,
  PackageReceiveIcon,
  Ticket01Icon,
  UserAdd01Icon,
  UserGroupIcon,
  Clock01Icon,
  ComputerIcon,
  Login03Icon,
  Shield01Icon,
} from '@hugeicons/core-free-icons';
import type { IconSvgElement } from '@hugeicons/react';
import type { View } from '../../app/App';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { TableActionButton } from '../../components/ui/TableActionButton';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { OperationalTableCard } from '../../components/ui/operational-table-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { ConsoleState, CustomerIdentityHealth } from '../../lib/api';

export function DashboardPage({ identityHealth, state, setView }: { identityHealth: CustomerIdentityHealth | null; state: ConsoleState; setView: (view: View) => void }) {
  const activeAdmins = state.members.filter((member) => member.role === 'org_admin' && member.status === 'active').length;
  const pendingMembers = state.members.filter((member) => member.status === 'pending_activation');
  const openTickets = state.tickets.filter((ticket) => ticket.status !== 'closed').length;
  const latestRelease = state.releases[0];
  const accessQueue = [...pendingMembers.slice(0, 3), ...state.userRequests.slice(0, 2)];

  return (
    <section className="flex flex-col gap-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard detail="Can manage the organization console" icon={UserGroupIcon} iconTone="bg-teal-500/12 text-teal-700" label="Active admins" onClick={() => setView('users')} value={String(activeAdmins)} />
        <MetricCard detail="Invitations awaiting acceptance" icon={UserAdd01Icon} iconTone="bg-sky-500/12 text-sky-700" label="Pending activation" onClick={() => setView('users')} value={String(pendingMembers.length)} />
        <MetricCard detail={state.license?.expiryDate ? `Expires ${state.license.expiryDate}` : 'No expiry configured'} icon={LicenseIcon} iconTone="bg-violet-500/12 text-violet-700" label="License" onClick={() => setView('license')} value={state.license?.status ?? 'Unknown'} />
        <MetricCard detail={openTickets === 1 ? 'Conversation needs attention' : 'Support conversations need attention'} icon={Ticket01Icon} iconTone="bg-amber-500/14 text-amber-700" label="Open tickets" onClick={() => setView('tickets')} value={String(openTickets)} />
      </div>

      <AuthenticationHealthPanel health={identityHealth} />

      <OperationalTableCard
        action={<Button onClick={() => setView('users')} size="sm" variant="outline">Manage users</Button>}
        information={accessQueue.length ? `${accessQueue.length} ${accessQueue.length === 1 ? 'item needs' : 'items need'} attention.` : 'No invitations or requests are waiting.'}
        informationLabel="Access queue information"
        title="Access queue"
      >
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-4">Requester</TableHead>
              <TableHead>Request</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="pr-4 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingMembers.slice(0, 3).map((member) => (
              <TableRow key={member.id}>
                <TableCell className="pl-4"><p className="font-semibold">{member.fullName}</p><p className="text-xs text-muted-foreground">{member.email}</p></TableCell>
                <TableCell>Invitation</TableCell>
                <TableCell><StatusPill status={member.status} /></TableCell>
                <TableCell className="pr-4 text-right"><TableActionButton onClick={() => setView('users')}>Review</TableActionButton></TableCell>
              </TableRow>
            ))}
            {state.userRequests.slice(0, 2).map((request) => (
              <TableRow key={request.id}>
                <TableCell className="pl-4"><p className="font-semibold">{request.fullName}</p><p className="text-xs text-muted-foreground">{request.email}</p></TableCell>
                <TableCell>Seat approval</TableCell>
                <TableCell><StatusPill status={request.status} /></TableCell>
                <TableCell className="pr-4 text-right"><TableActionButton onClick={() => setView('users')}>Review</TableActionButton></TableCell>
              </TableRow>
            ))}
            {!accessQueue.length ? <TableRow><TableCell className="h-20 text-center text-muted-foreground" colSpan={4}>Access is up to date.</TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </OperationalTableCard>

      <OperationalTableCard
        action={<StatusPill className="bg-emerald-200/90 text-emerald-950 ring-1 ring-emerald-700/15" status={state.license?.status ?? 'unknown'} />}
        information="Build version, license validity, and authorized deployment targets."
        informationLabel="Deployment readiness information"
        title="Deployment readiness"
      >
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-4">Check</TableHead>
              <TableHead>Current</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="pr-4 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="pl-4"><div className="flex items-center gap-2"><span className="flex size-7 items-center justify-center rounded-lg bg-primary text-primary-foreground"><HugeIcon icon={PackageReceiveIcon} size={15} /></span><span className="font-semibold">Latest build</span></div></TableCell>
              <TableCell>{latestRelease ? `myPDF ${latestRelease.version}` : 'No build published'}</TableCell>
              <TableCell><StatusPill status={state.license?.releaseChannel ?? 'stable'} /></TableCell>
              <TableCell className="pr-4 text-right"><TableActionButton onClick={() => setView('updates')}>Updates</TableActionButton></TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="pl-4"><div className="flex items-center gap-2"><span className="flex size-7 items-center justify-center rounded-lg bg-violet-500/12 text-violet-700"><HugeIcon icon={LicenseIcon} size={15} /></span><span className="font-semibold">License validity</span></div></TableCell>
              <TableCell>Expires {state.license?.expiryDate ?? 'not set'}</TableCell>
              <TableCell><StatusPill status={state.license?.status ?? 'unknown'} /></TableCell>
              <TableCell className="pr-4 text-right"><TableActionButton onClick={() => setView('license')}>License</TableActionButton></TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="pl-4"><div className="flex items-center gap-2"><span className="flex size-7 items-center justify-center rounded-lg bg-sky-500/12 text-sky-700"><HugeIcon icon={UserGroupIcon} size={15} /></span><span className="font-semibold">Deployment targets</span></div></TableCell>
              <TableCell>{state.organization.domain}</TableCell>
              <TableCell><StatusPill status={`${state.license?.licensedDomains.length ?? 0} authorized`} /></TableCell>
              <TableCell className="pr-4 text-right"><TableActionButton onClick={() => setView('updates')}>Manage</TableActionButton></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </OperationalTableCard>
    </section>
  );
}

function AuthenticationHealthPanel({ health }: { health: CustomerIdentityHealth | null }) {
  const max = Math.max(1, ...(health?.series.map((point) => point.successful + point.failed + point.risky) ?? [1]));
  const lastLogin = health?.currentUser.lastLoginAt
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(health.currentUser.lastLoginAt))
    : 'Not available';
  const metrics = [
    { label: 'Active sessions', value: health?.currentUser.liveStatus === 'ok' ? String(health.currentUser.activeSessionCount ?? 0) : 'Unavailable', icon: ComputerIcon, tone: 'bg-sky-500/12 text-sky-700' },
    { label: 'Last login', value: lastLogin, icon: Clock01Icon, tone: 'bg-violet-500/12 text-violet-700' },
    { label: 'Successful sign-ins', value: health ? String(health.summary.successful) : '—', icon: Login03Icon, tone: 'bg-teal-500/12 text-teal-700' },
    { label: 'Success rate', value: health?.summary.successRate === null || !health ? '—' : `${health.summary.successRate}%`, icon: Shield01Icon, tone: 'bg-amber-500/14 text-amber-700' },
  ];
  return (
    <Card className="gap-5 overflow-visible border-0 bg-transparent p-0 shadow-none" size="sm">
      <CardHeader className="bg-transparent p-0">
        <CardTitle>Authentication health</CardTitle>
        <p className="text-xs text-muted-foreground">Organization-level activity for the last 30 days. No device or location data is retained.</p>
      </CardHeader>
      <CardContent className="space-y-5 bg-transparent p-0">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => <div className="rounded-xl border bg-card p-3" key={metric.label}><div className="flex items-center gap-2"><span className={`flex size-7 items-center justify-center rounded-lg ${metric.tone}`}><HugeIcon icon={metric.icon} size={15} /></span><p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{metric.label}</p></div><p className="mt-3 truncate text-lg font-semibold">{metric.value}</p></div>)}
        </div>
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"><span>Daily authentication activity</span><span className="flex items-center gap-3"><span className="flex items-center gap-1.5"><i className="size-2 rounded-full bg-teal-500" />Successful</span><span className="flex items-center gap-1.5"><i className="size-2 rounded-full bg-red-400" />Failed</span><span className="flex items-center gap-1.5"><i className="size-2 rounded-full bg-amber-400" />Risky</span></span></div>
          <div className="flex h-36 items-end gap-1 rounded-xl border bg-white px-3 pb-3 pt-5" role="img" aria-label="Thirty-day authentication health chart">
            {(health?.series ?? Array.from({ length: 30 }, (_, index) => ({ date: String(index), successful: 0, failed: 0, risky: 0 }))).map((point) => {
              const total = point.successful + point.failed + point.risky;
              return <div className="group relative flex h-full min-w-0 flex-1 flex-col justify-end" key={point.date} title={`${point.date}: ${point.successful} successful, ${point.failed} failed, ${point.risky} risky`}><div className="w-full overflow-hidden rounded-sm bg-muted" style={{ height: `${Math.max(total ? 5 : 2, (total / max) * 100)}%` }}><div className="w-full bg-teal-500" style={{ height: `${total ? (point.successful / total) * 100 : 0}%` }} /><div className="w-full bg-red-400" style={{ height: `${total ? (point.failed / total) * 100 : 0}%` }} /><div className="w-full bg-amber-400" style={{ height: `${total ? (point.risky / total) * 100 : 0}%` }} /></div></div>;
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricCard({ detail, icon, iconTone, label, value, onClick }: { detail: string; icon: IconSvgElement; iconTone: string; label: string; value: string; onClick: () => void }) {
  return (
    <button className="group rounded-xl text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30" onClick={onClick} type="button">
      <Card className="h-full gap-3 py-3 transition-colors group-hover:bg-muted/30" size="sm">
        <CardContent className="flex h-full flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2"><span className={`flex size-7 items-center justify-center rounded-lg ${iconTone}`}><HugeIcon icon={icon} size={15} strokeWidth={1.9} /></span><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p></div>
            <HugeIcon className="text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" icon={ArrowRight01Icon} size={14} />
          </div>
          <p className="text-2xl font-semibold capitalize tracking-tight">{value}</p>
          <p className="mt-auto text-xs text-muted-foreground">{detail}</p>
        </CardContent>
      </Card>
    </button>
  );
}
